import { useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import { Link } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import PricingTable from '../Components/Pricing';

// Icons
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import InsightsIcon from '@mui/icons-material/Insights';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import EmailIcon from '@mui/icons-material/Email';
import StorageIcon from '@mui/icons-material/Storage';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SecurityIcon from '@mui/icons-material/Security';
import ShieldIcon from '@mui/icons-material/Shield';
import TimerIcon from '@mui/icons-material/Timer';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import WarningIcon from '@mui/icons-material/Warning';

// Images
import ms365Image from 'assets/images/illustrations/Microsoft-365-copy.jpg';
import ms365Graphs from 'assets/images/platform/ms365_graphs.png';
import sharepointGraph from 'assets/images/platform/sharepoint_file_graph.jpg';

export default function Microsoft365() {
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

    useEffect(() => {
        document.title = 'Intelligent Security for Microsoft 365 | PREVAIL';
        window.scrollTo(0, 0);
    });

    return (
        <>
            {/* ─── HERO ─── */}
            <Box
                width="100%"
                sx={{
                    minHeight: isMobile ? '100vh' : '72vh',
                    background: 'linear-gradient(160deg, #000000 0%, #1a0000 50%, #242331 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={2} p={3} mt={8} justifyContent="center" alignItems="center">
                    <Grid item xs={12} md={7} p={2}>
                        <Typography
                            sx={{ typography: { xs: 'h4', md: 'h2' }, mb: 2 }}
                            fontWeight="bold"
                            color="grey.100"
                        >
                            Your Microsoft 365 Tenant Is a Target.
                        </Typography>
                        <Typography
                            sx={{ typography: { xs: 'h6', md: 'h5' }, mb: 1 }}
                            color="prevailRed.main"
                            fontWeight="bold"
                        >
                            PREVAIL Stops Cyberattacks in Under 1 Minute.
                        </Typography>
                        <Typography
                            variant="h6"
                            color="grey.400"
                            sx={{ mb: 4, mt: 2, lineHeight: 1.8 }}
                        >
                            Phishing, credential theft, and insider threats hit small businesses every day.
                            PREVAIL uses proprietary AI to detect active breaches against your Microsoft 365
                            tenant — before attackers can steal your data.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Link to="https://app.prevailcyber.com/sign-up" target="_blank">
                                <Button
                                    variant="contained"
                                    color="prevailRed"
                                    size="large"
                                    sx={{ pl: 5, pr: 5, py: 1.5 }}
                                >
                                    <Typography sx={{ fontSize: '1.1em' }} color="grey.100">
                                        Start Free Trial
                                    </Typography>
                                </Button>
                            </Link>
                            <Link to="#how-it-works">
                                <Button
                                    variant="outlined"
                                    size="large"
                                    sx={{ pl: 4, pr: 4, py: 1.5, borderColor: 'grey.600', color: 'grey.300' }}
                                >
                                    <Typography sx={{ fontSize: '1.1em' }}>See How It Works</Typography>
                                </Button>
                            </Link>
                        </Box>
                    </Grid>
                    
                </Grid>
            </Box>

            {/* ─── STATS BAR ─── */}
            <Box sx={{ background: '#6b0000', py: 4 }}>
                <Grid container maxWidth="xl" spacing={2} justifyContent="center" alignItems="center"
                    textAlign="center" sx={{ mx: 'auto' }}>
                    {[
                        { stat: '$4.45M', label: 'Average cost of a data breach' },
                        { stat: '328 Days', label: 'Average time to detect a breach' },
                        { stat: '94%', label: 'Companies need stronger MS365 security' },
                        { stat: '< 1 Min', label: 'PREVAIL threat detection time' },
                    ].map(({ stat, label }) => (
                        <Grid item size={{ xs: 6, md: 3 }} key={stat}>
                            <Typography variant="h4" color="grey.100" fontWeight="bold">{stat}</Typography>
                            <Typography variant="body2" color="grey.400" mt={0.5}>{label}</Typography>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* ─── THE THREATS ─── */}
            <Box
                sx={{
                    background: '#242331',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={2} justifyContent="center">
                    <Grid item xs={12} textAlign="center" mb={4}>
                        <Typography variant="h3" color="grey.100" fontWeight="bold">
                            Attackers Are After Your Business Data
                        </Typography>
                        <Typography variant="h6" color="grey.400" mt={2} sx={{ maxWidth: '700px', mx: 'auto' }}>
                            Small businesses are now the #1 target for cybercriminals. Microsoft 365 —
                            your email, files, and Teams — is the primary attack surface.
                        </Typography>
                    </Grid>

                    {[
                        {
                            icon: <EmailIcon sx={{ fontSize: 48 }} />,
                            title: 'Account Takeover',
                            description:
                                'Phishing emails trick your employees into giving up their passwords. Once attackers are in, they move quietly through your emails, files, and contacts — often for weeks before anyone notices.',
                            examples: ['Phishing & spear-phishing', 'MFA bypass & exhaustion attacks', 'Transparent proxy credential harvesting'],
                        },
                        {
                            icon: <StorageIcon sx={{ fontSize: 48 }} />,
                            title: 'Data Exfiltration',
                            description:
                                'Stolen credentials, overshared SharePoint folders, and third-party app integrations can silently expose your most sensitive business and client data to the outside world.',
                            examples: ['Mass unauthorized file downloads', 'SharePoint & OneDrive oversharing', 'Third-party app vulnerabilities'],
                        },
                        {
                            icon: <PeopleIcon sx={{ fontSize: 48 }} />,
                            title: 'Insider Threats',
                            description:
                                'A departing employee, a careless click, or a bribed insider can compromise everything. Intentional or accidental, insider actions are among the hardest threats to detect — until now.',
                            examples: ['Data theft by departing staff', 'Accidental sensitive data exposure', 'Admin privilege misuse'],
                        },
                    ].map(({ icon, title, description, examples }) => (
                        <Grid item xs={12} md={4} key={title} p={1}>
                            <Card
                                sx={{
                                    background: '#1a1929',
                                    border: '1px solid rgba(217,0,0,0.35)',
                                    borderRadius: 2,
                                    height: '100%',
                                }}
                            >
                                <CardContent sx={{ p: 4 }}>
                                    <Typography color="prevailRed.main" mb={2}>{icon}</Typography>
                                    <Typography variant="h5" color="grey.100" fontWeight="bold" mb={2}>
                                        {title}
                                    </Typography>
                                    <Typography variant="body2" color="grey.400" mb={3} lineHeight={1.8}>
                                        {description}
                                    </Typography>
                                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 2 }} />
                                    {examples.map((ex) => (
                                        <Box key={ex} display="flex" alignItems="center" gap={1} mb={1}>
                                            <WarningIcon sx={{ fontSize: 16, color: '#DB9D47' }} />
                                            <Typography variant="caption" color="grey.400">{ex}</Typography>
                                        </Box>
                                    ))}
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* ─── WHY MS365 ALONE ISN'T ENOUGH ─── */}
            <Box
                sx={{
                    background: '#f8f9fa',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={6} justifyContent="center" alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Typography variant="h3" color="grey.800" fontWeight="bold" mb={3}>
                            Microsoft 365 Has Security.
                            <br />
                            <Typography component="span" variant="h3" color="prevailRed.main" fontWeight="bold">
                                It's Just Not Enough.
                            </Typography>
                        </Typography>
                        <Typography variant="body1" color="grey.700" mb={3} sx={{ fontSize: '1.1em', lineHeight: 1.8 }}>
                            Microsoft 365's security tools are built for large enterprises with dedicated
                            IT staff. For a small business, properly configuring and maintaining these tools
                            is a full-time job — and one wrong setting leaves the door open.
                        </Typography>
                        <Typography variant="body1" color="grey.700" mb={4} sx={{ fontSize: '1.1em', lineHeight: 1.8 }}>
                            Worse, most security systems focus on <strong>preventing</strong> attacks — not detecting
                            breaches already in progress. By the time you know something is wrong,
                            the damage is done.
                        </Typography>
                        {[
                            'Complex configuration requires dedicated IT expertise',
                            "Rule-based alerts miss sophisticated modern attacks",
                            "No behavioral baseline — can't detect subtle compromise",
                            "No visibility into what users are doing with your data",
                        ].map((point) => (
                            <Box key={point} display="flex" alignItems="flex-start" gap={1.5} mb={1.5}>
                                <CancelIcon sx={{ color: '#d90000', mt: 0.3, fontSize: 20, flexShrink: 0 }} />
                                <Typography variant="body2" color="grey.700" sx={{ fontSize: '1em' }}>{point}</Typography>
                            </Box>
                        ))}
                    </Grid>
                    <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Box
                            sx={{
                                backgroundImage: `url(${ms365Image})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                height: '420px',
                                borderRadius: 2,
                                boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* ─── HOW PREVAIL WORKS ─── */}
            <Box
                id="how-it-works"
                sx={{
                    background: '#ffffff',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={2} justifyContent="center">
                    <Grid item xs={12} textAlign="center" mb={6}>
                        <Typography
                            variant="overline"
                            color="prevailRed.main"
                            fontWeight="bold"
                            sx={{ letterSpacing: 3, fontSize: '0.9em' }}
                        >
                            HOW IT WORKS
                        </Typography>
                        <Typography variant="h3" color="grey.800" fontWeight="bold" mt={1}>
                            Always On. Always Watching.
                        </Typography>
                        <Typography variant="h6" color="grey.600" mt={2} sx={{ maxWidth: '650px', mx: 'auto' }}>
                            PREVAIL connects to your Microsoft 365 tenant in minutes and immediately starts
                            building behavioral intelligence — no configuration required.
                        </Typography>
                    </Grid>

                    {[
                        {
                            icon: <CloudDownloadIcon sx={{ fontSize: 56 }} />,
                            step: '01',
                            title: 'Connect & Collect',
                            description:
                                'Add your Microsoft 365 tenant in under 3 minutes via a secure Azure enterprise application. PREVAIL immediately begins collecting sign-in logs, Microsoft Graph data, and user activity — as frequently as every minute.',
                        },
                        {
                            icon: <InsightsIcon sx={{ fontSize: 56 }} />,
                            step: '02',
                            title: 'Build Behavioral Profiles',
                            description:
                                'Our proprietary machine learning algorithms build unique behavioral profiles for every user and your entire tenant. We learn how your business normally operates so we can instantly spot when something is wrong.',
                        },
                        {
                            icon: <NotificationImportantIcon sx={{ fontSize: 56 }} />,
                            step: '03',
                            title: 'Alert in Under 1 Minute',
                            description:
                                "The moment an attack pattern is detected — whether it's a stolen credential, suspicious file download, or insider moving data — PREVAIL sends an immediate alert so you can act before damage is done.",
                        },
                    ].map(({ icon, step, title, description }) => (
                        <Grid item xs={12} md={4} key={step} p={2}>
                            <Box
                                sx={{
                                    border: '2px solid',
                                    borderColor: 'grey.200',
                                    borderRadius: 2,
                                    p: 4,
                                    height: '100%',
                                    position: 'relative',
                                    transition: 'border-color 0.3s',
                                    '&:hover': { borderColor: '#d90000' },
                                }}
                            >
                                <Typography
                                    sx={{
                                        position: 'absolute',
                                        top: 12,
                                        right: 18,
                                        fontSize: '3.5em',
                                        fontWeight: 900,
                                        color: 'rgba(217,0,0,0.07)',
                                        lineHeight: 1,
                                        userSelect: 'none',
                                    }}
                                >
                                    {step}
                                </Typography>
                                <Typography color="primary.red" mb={2}>{icon}</Typography>
                                <Typography variant="h5" color="grey.800" fontWeight="bold" mb={2}>{title}</Typography>
                                <Typography variant="body2" color="grey.600" lineHeight={1.8} sx={{ fontSize: '1em' }}>
                                    {description}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* ─── PLATFORM SCREENSHOTS ─── */}
            <Box
                sx={{
                    background: '#242331',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={6} justifyContent="center" alignItems="center">
                    <Grid item size={{ xs: 12}} textAlign="center" mb={2}>
                        <Typography
                            variant="overline"
                            color="prevailRed.main"
                            fontWeight="bold"
                            sx={{ letterSpacing: 3, fontSize: '0.9em' }}
                        >
                            THE PLATFORM
                        </Typography>
                        <Typography variant="h3" color="grey.100" fontWeight="bold" mt={1}>
                            Total Visibility. Zero Complexity.
                        </Typography>
                    </Grid>

                    <Grid item size={{ xs: 12, md: 6 }} order={{ xs: 2, md: 1 }}>
                        <Typography variant="h5" color="grey.200" fontWeight="bold" mb={3}>
                            See Every User. Catch Every Threat.
                        </Typography>
                        <Typography variant="body1" color="grey.400" mb={3} lineHeight={1.8} sx={{ fontSize: '1.05em' }}>
                            Your PREVAIL dashboard gives you a real-time window into everything happening
                            across your Microsoft 365 environment. No security expertise required.
                        </Typography>
                        {[
                            { icon: <ShieldIcon sx={{ fontSize: 20, color: '#d90000' }} />, text: 'Real-time alerts when accounts are compromised' },
                            { icon: <DataUsageIcon sx={{ fontSize: 20, color: '#d90000' }} />, text: 'Per-user behavioral analytics and activity reports' },
                            { icon: <AccountCircleIcon sx={{ fontSize: 20, color: '#d90000' }} />, text: 'Track file access, sharing, and sign-in patterns' },
                            { icon: <TimerIcon sx={{ fontSize: 20, color: '#d90000' }} />, text: 'Historical trend data for audits and compliance' },
                            { icon: <SecurityIcon sx={{ fontSize: 20, color: '#d90000' }} />, text: 'Customizable alert notifications by email' },
                        ].map(({ icon, text }) => (
                            <Box key={text} display="flex" alignItems="flex-start" gap={1.5} mb={2}>
                                {icon}
                                <Typography variant="body2" color="grey.300" sx={{ fontSize: '1em' }}>{text}</Typography>
                            </Box>
                        ))}
                    </Grid>
                    <Grid item size={{ xs: 12, md: 6 }} order={{ xs: 1, md: 2 }}>
                        <Box 
                            component="img"
                            src={ms365Graphs}
                            alt="PREVAIL Graphs"
                            sx={{
                                width: '100%', height: 'auto', display: 'block'
                            }}
                        />
                    </Grid>
                </Grid>

                <Grid container maxWidth="lg" spacing={6} justifyContent="center" alignItems="center" mt={8}>
                    <Grid item size={{ xs: 12, md: 6 }}>
                        <Box
                            component="img"
                            src={sharepointGraph}
                            alt="PREVAIL SharePoint Graph"
                            sx={{
                                 width: '100%', height: 'auto', display: 'block'
                            }}
                        />
                    </Grid>
                    <Grid item size={{ xs: 12, md: 6 }}>
                        <Typography variant="h5" color="grey.200" fontWeight="bold" mb={3}>
                            Monitor SharePoint, Teams &amp; OneDrive
                        </Typography>
                        <Typography variant="body1" color="grey.400" mb={3} lineHeight={1.8} sx={{ fontSize: '1.05em' }}>
                            PREVAIL tracks file activity across your entire Microsoft 365 environment —
                            including SharePoint, Teams, and OneDrive. See exactly who is accessing,
                            downloading, and sharing your sensitive data.
                        </Typography>
                        <Typography variant="body1" color="grey.400" lineHeight={1.8} sx={{ fontSize: '1.05em' }}>
                            When behavior breaks from the norm — a user suddenly downloading hundreds of
                            files, or sharing folders externally — PREVAIL catches it immediately and
                            alerts you before data leaves your organization.
                        </Typography>
                    </Grid>
                </Grid>
            </Box>

            {/* ─── PREVAIL vs MICROSOFT BUILT-IN ─── */}
            <Box
                sx={{
                    background: '#f8f9fa',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={4} justifyContent="center">
                    <Grid item xs={12} textAlign="center" mb={4}>
                        <Typography variant="h3" color="grey.800" fontWeight="bold">
                            PREVAIL vs. Built-In Microsoft Security
                        </Typography>
                        <Typography variant="h6" color="grey.600" mt={2} sx={{ maxWidth: '650px', mx: 'auto' }}>
                            Microsoft's tools are designed for enterprise IT teams.
                            PREVAIL is designed for businesses like yours.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Card sx={{ border: '2px solid #e0e0e0', borderRadius: 2, height: '100%' }}>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="h5" color="grey.700" fontWeight="bold" mb={3}>
                                    Microsoft Built-In Security
                                </Typography>
                                {[
                                    'Requires expert configuration and ongoing maintenance',
                                    'Rule-based alerts generate excessive false positives',
                                    'Detects only known, pre-defined attack signatures',
                                    'No per-user behavioral baseline',
                                    'Designed for large enterprises with dedicated IT staff',
                                    'Security features often require expensive add-on licensing',
                                ].map((point) => (
                                    <Box key={point} display="flex" alignItems="flex-start" gap={1.5} mb={2}>
                                        <CancelIcon sx={{ color: '#d90000', mt: 0.2, fontSize: 20, flexShrink: 0 }} />
                                        <Typography variant="body2" color="grey.600" sx={{ fontSize: '0.95em' }}>{point}</Typography>
                                    </Box>
                                ))}
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Card sx={{ border: '2px solid #d90000', borderRadius: 2, height: '100%' }}>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="h5" color="prevailRed.main" fontWeight="bold" mb={3}>
                                    PREVAIL for Microsoft 365
                                </Typography>
                                {[
                                    'Connected and protecting in under 3 minutes',
                                    'AI-powered behavioral analytics — fewer false alarms',
                                    'Detects novel attacks, not just known signatures',
                                    'Unique behavioral profile for every user and tenant',
                                    'Built specifically for SMBs — no IT team required',
                                    'Simple, transparent pricing starting at $2/user/month',
                                ].map((point) => (
                                    <Box key={point} display="flex" alignItems="flex-start" gap={1.5} mb={2}>
                                        <CheckCircleIcon sx={{ color: '#47A025', mt: 0.2, fontSize: 20, flexShrink: 0 }} />
                                        <Typography variant="body2" color="grey.700" sx={{ fontSize: '0.95em' }}>{point}</Typography>
                                    </Box>
                                ))}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* ─── TESTIMONIALS ─── */}
            <Box
                sx={{
                    background: '#6b0000',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={4} justifyContent="center">
                    <Grid item xs={12} textAlign="center" mb={4}>
                        <Typography variant="h3" color="grey.100" fontWeight="bold">
                            Trusted by Small Businesses
                        </Typography>
                    </Grid>
                    {[
                        {
                            quote:
                                "PREVAIL not only protects us from account takeover and other attacks, but we can also easily review the activity of outgoing personnel to ensure our intellectual property is secure.",
                            name: "President & CEO",
                            company: "Landscape Architecture Firm",
                        },
                        {
                            quote:
                                "Prior to enabling 2-step authentication, someone got into my account early on a Sunday morning. Within minutes, the MSP was notified and locked my account to prevent access to our research data.",
                            name: "Senior Vice President",
                            company: "Medical Research Organization",
                        },
                        {
                            quote:
                                "PREVAIL provides a simple way to secure our customers and report on individual activity, easily switching between tenants. It takes less than a minute to add a tenant to the platform.",
                            name: "Principal",
                            company: "Managed IT Services Provider",
                        },
                    ].map(({ quote, name, company }) => (
                        <Grid item xs={12} md={4} key={name}>
                            <Card
                                sx={{
                                    background: '#343246',
                                    border: '1px solid rgb(66, 64, 90)',
                                    borderRadius: 2,
                                    height: '100%',
                                }}
                            >
                                <CardContent sx={{ p: 4 }}>
                                    <Typography
                                        color="prevailRed.main"
                                        sx={{ lineHeight: 0.8, mb: 2, fontSize: '3.5em', fontWeight: 900 }}
                                    >
                                        "
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        color="grey.300"
                                        mb={3}
                                        lineHeight={1.8}
                                        sx={{ fontStyle: 'italic' }}
                                    >
                                        {quote}
                                    </Typography>
                                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', mb: 2 }} />
                                    <Typography variant="body2" color="grey.200" fontWeight="bold">{name}</Typography>
                                    <Typography variant="caption" color="grey.500">{company}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* ─── PRICING ─── */}
           <PricingTable />

            {/* ─── FINAL CTA ─── */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #2D0000 0%, #6b0000 60%, #242331 100%)',
                    py: 12,
                    display: 'grid',
                    placeItems: 'center',
                    textAlign: 'center',
                }}
            >
                <Grid container maxWidth="md" spacing={2} justifyContent="center">
                    <Grid item xs={12}>
                        <Typography variant="h3" color="grey.100" fontWeight="bold" mb={2}>
                            Start Protecting Your Business Today
                        </Typography>
                        <Typography variant="h6" color="grey.400" mb={5} sx={{ maxWidth: '580px', mx: 'auto' }}>
                            Connect your Microsoft 365 tenant in under 3 minutes.
                            No security expertise needed. No credit card required.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Link to="https://app.prevailcyber.com/sign-up" target="_blank">
                                <Button
                                    variant="contained"
                                    color="prevailRed"
                                    size="large"
                                    sx={{ pl: 6, pr: 6, py: 2 }}
                                >
                                    <Typography sx={{ fontSize: '1.2em' }} color="grey.100">
                                        Start Free Trial
                                    </Typography>
                                </Button>
                            </Link>
                            <Link to="/#contact">
                                <Button
                                    variant="outlined"
                                    size="large"
                                    sx={{ pl: 5, pr: 5, py: 2, borderColor: 'grey.500', color: 'grey.300' }}
                                >
                                    <Typography sx={{ fontSize: '1.2em' }}>Contact Us</Typography>
                                </Button>
                            </Link>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
}
